import React from "react";
import { Check, ArrowDown, ArrowRight, Sprout, Globe, CreditCard, Cog, Truck, Box, Heart, Leaf } from "lucide-react";

/* ---------------------------------------------------------------------------
   ChapterBlocks, renders structured visual blocks within a chapter.
   Block types:
     - text { type:'text', content }
     - heading { type:'heading', content } section divider
     - tagline { type:'tagline', content } centered hero line
     - callout { type:'callout', content } indented quote
     - two_column { type:'two_column', left:{title,items}, right:{title,items}, footer? }
     - flow { type:'flow', steps:[string], footer? } vertical arrow flow
     - checklist { type:'checklist', title?, rows:[{label,value}], footer? }
     - infographic { type:'infographic', title?, steps:[{icon,label}] } horizontal
   --------------------------------------------------------------------------- */

const ICONS = {
  customer: Heart,
  globe: Globe,
  card: CreditCard,
  cog: Cog,
  sprout: Sprout,
  box: Box,
  truck: Truck,
};

export default function ChapterBlocks({ blocks }) {
  return (
    <div className="space-y-2">
      {blocks.map((b, i) => <Block key={`${b.type}-${i}`} block={b} />)}
    </div>
  );
}

function Block({ block }) {
  switch (block.type) {
    case "text":
      return (
        <div className="bloom-prose">
          {block.content.split("\n\n").map((p, i) => <p key={`p-${i}-${p.length}`}>{p.trim()}</p>)}
        </div>
      );

    case "heading":
      return <div className="bloom-divider my-10">{block.content}</div>;

    case "tagline":
      return (
        <p className="font-serif italic text-2xl md:text-3xl text-center text-[var(--bloom-brand)] my-10 leading-snug">
          {block.content}
        </p>
      );

    case "callout":
      return (
        <blockquote className="my-8 border-l-2 border-[var(--bloom-brand)] pl-6 py-2 font-serif italic text-xl text-[var(--bloom-ink-soft)] leading-relaxed">
          {block.content}
        </blockquote>
      );

    case "two_column":
      return (
        <div className="my-10 grid md:grid-cols-2 gap-px bg-[var(--bloom-border)] border border-[var(--bloom-border)]">
          <ColumnPanel title={block.left.title} items={block.left.items} />
          <ColumnPanel title={block.right.title} items={block.right.items} />
          {block.footer && (
            <p className="col-span-2 bg-[var(--bloom-bg)] p-6 text-center font-serif italic text-lg text-[var(--bloom-ink-soft)]">
              {block.footer}
            </p>
          )}
        </div>
      );

    case "flow":
      return (
        <div className="my-10">
          <div className="space-y-3 max-w-md mx-auto">
            {block.steps.map((step, i) => (
              <React.Fragment key={`step-${i}-${String(step).slice(0, 24)}`}>
                <div className="border border-[var(--bloom-border)] bg-[var(--bloom-bg)] py-4 px-6 text-center">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)] mr-3">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-serif text-lg">{step}</span>
                </div>
                {i < block.steps.length - 1 && (
                  <div className="flex justify-center">
                    <ArrowDown size={18} className="text-[var(--bloom-brand)]" strokeWidth={1.5} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          {block.footer && (
            <p className="text-center font-serif italic text-base text-[var(--bloom-ink-soft)] mt-8 max-w-xl mx-auto">
              {block.footer}
            </p>
          )}
        </div>
      );

    case "checklist": {
      const headers = block.headers || ["Item", "Grower Retains Control"];
      const showChecks = block.show_checks !== false;
      return (
        <div className="my-10">
          {block.title && (
            <h4 className="font-serif text-2xl mb-6 text-center">{block.title}</h4>
          )}
          <div className="border border-[var(--bloom-border)] overflow-hidden max-w-2xl mx-auto">
            <div className="grid grid-cols-[1fr_auto] bg-[var(--bloom-surface)] border-b border-[var(--bloom-border)] px-5 py-3 text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">
              <span>{headers[0]}</span>
              <span>{headers[1]}</span>
            </div>
            {block.rows.map((r, i) => (
              <div
                key={`${r.label}-${i}`}
                className={`grid grid-cols-[1fr_auto] px-5 py-3 items-center ${i < block.rows.length - 1 ? "border-b border-[var(--bloom-border)]" : ""}`}
              >
                <span className="font-serif text-base">{r.label}</span>
                <span className={`flex items-center gap-2 ${showChecks ? "text-[var(--bloom-brand)]" : "text-[var(--bloom-ink)]"}`}>
                  {showChecks && <Check size={16} strokeWidth={2} />}
                  <span className={showChecks ? "text-sm" : "font-serif text-base"}>{r.value}</span>
                </span>
              </div>
            ))}
          </div>
          {block.footer && (
            <p className="text-center font-serif italic text-base text-[var(--bloom-ink-soft)] mt-6 max-w-xl mx-auto">
              {block.footer}
            </p>
          )}
        </div>
      );
    }

    case "funnel":
      return (
        <div className="my-12">
          {block.title && (
            <h4 className="font-serif text-2xl mb-8 text-center">{block.title}</h4>
          )}
          <div className="space-y-2 max-w-lg mx-auto">
            {block.steps.map((s, i) => {
              const widthPct = 100 - i * (70 / Math.max(1, block.steps.length - 1));
              return (
                <React.Fragment key={`funnel-${s.number}-${i}`}>
                  <div
                    className="bg-[var(--bloom-ink)] text-[var(--bloom-bg)] mx-auto py-4 px-6 flex items-baseline justify-between gap-6"
                    style={{ width: `${widthPct}%`, minWidth: "240px" }}
                  >
                    <span className="font-serif text-2xl">{s.number}</span>
                    <span className="text-[10px] tracking-[0.2em] uppercase opacity-80 text-right leading-tight">
                      {s.label}
                    </span>
                  </div>
                  {i < block.steps.length - 1 && (
                    <div className="flex justify-center">
                      <ArrowDown size={16} className="text-[var(--bloom-brand)]" strokeWidth={1.5} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
          {block.footer && (
            <p className="text-center font-serif italic text-base text-[var(--bloom-ink-soft)] mt-8 max-w-xl mx-auto">
              {block.footer}
            </p>
          )}
        </div>
      );

    case "infographic":
      return (
        <div className="my-12 p-6 md:p-10 bg-[var(--bloom-surface)] border border-[var(--bloom-border)]">
          {block.title && (
            <h4 className="font-serif text-2xl text-center mb-8">{block.title}</h4>
          )}
          <div className="flex flex-col md:flex-row md:items-center justify-center gap-3 md:gap-1 flex-wrap">
            {block.steps.map((s, i) => {
              const Icon = ICONS[s.icon] || Sprout;
              return (
                <React.Fragment key={`info-${s.label}-${i}`}>
                  <div className="flex md:flex-col items-center gap-3 md:gap-2 md:w-24 text-center">
                    <div className="w-12 h-12 rounded-full border border-[var(--bloom-brand)] flex items-center justify-center text-[var(--bloom-brand)] shrink-0">
                      <Icon size={20} strokeWidth={1.5} />
                    </div>
                    <span className="text-[10px] tracking-[0.15em] uppercase text-[var(--bloom-ink-soft)] leading-tight">
                      {s.label}
                    </span>
                  </div>
                  {i < block.steps.length - 1 && (
                    <ArrowRight
                      size={16}
                      strokeWidth={1.5}
                      className="text-[var(--bloom-ink-muted)] hidden md:block"
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      );

    case "nz_map":
      return <NZNetworkMap block={block} />;

    case "values":
      return (
        <div className="my-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 max-w-3xl mx-auto">
          {block.items.map((v, i) => (
            <div key={`value-${v.name}-${i}`} className="flex gap-4">
              <Leaf size={22} strokeWidth={1.5} className="text-[var(--bloom-brand)] mt-1 shrink-0" />
              <div>
                <h4 className="font-serif text-xl mb-1">{v.name}</h4>
                <p className="text-sm text-[var(--bloom-ink-soft)] leading-relaxed">{v.description}</p>
              </div>
            </div>
          ))}
        </div>
      );

    case "columns": {
      const count = block.columns.length;
      const colClass = count === 3
        ? "md:grid-cols-3"
        : count === 4
        ? "md:grid-cols-2 lg:grid-cols-4"
        : "md:grid-cols-2";
      return (
        <div className="my-10">
          <div className={`grid grid-cols-1 ${colClass} gap-px bg-[var(--bloom-border)] border border-[var(--bloom-border)]`}>
            {block.columns.map((c, i) => (
              <ColumnPanel key={`${c.title}-${i}`} title={c.title} items={c.items} />
            ))}
          </div>
          {block.footer && (
            <p className="text-center font-serif italic text-base text-[var(--bloom-ink-soft)] mt-6 max-w-xl mx-auto">
              {block.footer}
            </p>
          )}
        </div>
      );
    }

    case "roadmap":
      return (
        <div className="my-10 space-y-6 max-w-2xl mx-auto">
          {block.items.map((r, i) => (
            <React.Fragment key={`${r.year}-${i}`}>
              <div className="border border-[var(--bloom-border)] bg-[var(--bloom-bg)] p-6 md:p-8">
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-4">
                  <span className="font-serif text-3xl text-[var(--bloom-brand)]">{r.year}</span>
                  <span className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)]">
                    {r.phase}
                  </span>
                </div>
                <ul className="space-y-2 mb-5">
                  {r.bullets.map((b, j) => (
                    <li key={`${r.year}-bullet-${j}`} className="flex items-start gap-3 text-sm leading-relaxed">
                      <Leaf size={12} strokeWidth={1.5} className="text-[var(--bloom-brand)] mt-1.5 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="border-t border-[var(--bloom-border)] pt-4 font-serif italic text-base text-[var(--bloom-ink-soft)]">
                  Objective: {r.objective}
                </div>
              </div>
              {i < block.items.length - 1 && (
                <div className="flex justify-center">
                  <ArrowDown size={16} strokeWidth={1.5} className="text-[var(--bloom-brand)]" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      );

    case "table":
      return (
        <div className="my-8">
          {block.title && (
            <h4 className="font-serif text-2xl mb-4 text-center">{block.title}</h4>
          )}
          <div className="border border-[var(--bloom-border)] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--bloom-surface)] border-b border-[var(--bloom-border)]">
                <tr>
                  {block.headers.map((h, i) => (
                    <th
                      key={`${h}-${i}`}
                      className={`px-5 py-3 text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)] font-medium ${i === 0 ? "text-left" : "text-right"}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, i) => (
                  <tr
                    key={`row-${i}-${String(row[0] ?? "").slice(0, 16)}`}
                    className={i < block.rows.length - 1 ? "border-b border-[var(--bloom-border)]" : ""}
                  >
                    {row.map((cell, j) => (
                      <td
                        key={`cell-${i}-${j}`}
                        className={`px-5 py-3 ${j === 0 ? "font-serif text-base" : "text-right font-serif text-base"}`}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {block.footer && (
            <p className="text-center font-serif italic text-base text-[var(--bloom-ink-soft)] mt-4 max-w-xl mx-auto">
              {block.footer}
            </p>
          )}
        </div>
      );

    default:
      return null;
  }
}

function NZNetworkMap({ block }) {
  const phaseColors = {
    1: "var(--bloom-brand)",
    2: "var(--bloom-warning)",
    3: "var(--bloom-ink-muted)",
  };
  return (
    <div className="my-12 p-6 md:p-10 bg-[var(--bloom-surface)] border border-[var(--bloom-border)]">
      {block.title && (
        <h4 className="font-serif text-2xl text-center mb-2">{block.title}</h4>
      )}
      {block.subtitle && (
        <p className="text-center font-serif italic text-base text-[var(--bloom-ink-soft)] mb-8">
          {block.subtitle}
        </p>
      )}
      <div className="flex flex-col md:flex-row gap-8 items-center justify-center">
        <svg viewBox="0 0 220 440" className="w-44 md:w-56 h-auto" aria-label="Bloom Club regional network">
          {/* North Island */}
          <path
            d="M 130 20 Q 170 28 178 70 Q 188 105 172 142 L 168 168 Q 178 180 160 198 Q 148 218 128 232 L 112 234 Q 96 218 92 192 Q 88 158 96 124 Q 108 80 118 48 Q 122 28 130 20 Z"
            fill="var(--bloom-bg)"
            stroke="var(--bloom-ink-muted)"
            strokeWidth="1"
          />
          {/* South Island */}
          <path
            d="M 96 252 Q 134 246 148 274 Q 162 304 150 332 Q 138 366 110 384 L 84 388 Q 60 372 54 340 Q 50 308 64 282 Q 78 258 96 252 Z"
            fill="var(--bloom-bg)"
            stroke="var(--bloom-ink-muted)"
            strokeWidth="1"
          />
          {/* Dots + labels */}
          {block.regions.map((r, i) => (
            <g key={`${r.name}-${i}`}>
              <circle cx={r.x} cy={r.y} r="4.5" fill={phaseColors[r.phase] || phaseColors[1]} />
              <circle cx={r.x} cy={r.y} r="9" fill={phaseColors[r.phase] || phaseColors[1]} opacity="0.18" />
              <text
                x={r.label_x ?? r.x + 9}
                y={r.y + 3}
                fontSize="9"
                fontFamily="Work Sans, sans-serif"
                fill="var(--bloom-ink)"
                textAnchor={r.anchor || "start"}
                style={{ letterSpacing: "0.04em" }}
              >
                {r.name.toUpperCase()}
              </text>
            </g>
          ))}
        </svg>

        {/* Legend */}
        <div className="space-y-4 max-w-xs">
          {block.phases.map((p, i) => (
            <div key={`phase-${p.phase}-${i}`} className="flex items-start gap-3">
              <span
                className="w-3 h-3 rounded-full mt-1.5 shrink-0"
                style={{ background: phaseColors[p.phase] }}
              />
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">
                  Phase {p.phase}
                </div>
                <div className="font-serif text-base">{p.regions.join(" · ")}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {block.footer && (
        <p className="text-center font-serif italic text-base text-[var(--bloom-ink-soft)] mt-8">
          {block.footer}
        </p>
      )}
    </div>
  );
}

function ColumnPanel({ title, items }) {
  return (
    <div className="bg-[var(--bloom-bg)] p-6 md:p-8">
      <div className="bloom-label mb-5">{title}</div>
      <ul className="space-y-3">
        {items.map((it, i) => (
          <li key={`item-${i}-${String(it).slice(0, 24)}`} className="flex items-start gap-3 text-sm leading-relaxed">
            <Sprout size={14} className="text-[var(--bloom-brand)] mt-1 shrink-0" strokeWidth={1.5} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
